import { ColDef, RowClickedEvent, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { aggridHeaderClass } from "src/components/common/common";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { createSaveColumnState, defaultColDef, getDefaultRowIndexColumnConfig, getRowStyle, onGridReady } from "src/utils/grid/gridStyleUtils";

import { GridContextMenuWrapper } from "src/components/common/gridWrapper/GridContextMenuWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { KSK2070_R01Dto, defaultKSK2070_R01Dto, GridRowData, SUBTOTAL_TYPES } from "./Dto_KSK2070_R01";
import { createSubtotalGuards } from "src/utils/subtotal/guards";
import { useKSK2070_R01Store } from "./Store_KSK2070_R01";
import { formatDate } from "src/utils/string/stringUtil";
import { createCellCssClassRules } from "src/utils/grid/GridHandlerContent";

import { calculateSubtotals, calculateDateSubtotals, mergeDataWithSubtotals } from "./utils/subtotalUtils_KSK2070_R01";
import { formatNumber } from "src/utils/number/numberUtils";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { CommonCodeColumn, CompCdColumn } from "src/utils/grid/GridColumnContent";
import { cellDateComparator, onlyTouchGridStyle } from "src/utils/grid/gridFunctionUtil";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";

const { isSubtotalRow, isItemSubtotalRow, isDateSubtotalRow } = createSubtotalGuards<GridRowData>({
	isSubtotalKey: 'isSubtotal',
	typeKey: 'subtotalType',
	itemTypeValue: SUBTOTAL_TYPES.ITEM,
	dateTypeValue: SUBTOTAL_TYPES.DATE,
});

const COLUMN_STATE_KEY = 'KSK2070_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<GridRowData>>;
}

export const Table_KSK2070_R01 = (props: Props) => {

	const { gridRef } = props;

    const { 
		KSK2070_R01List,
		selectedKSK2070_R01List,  addSelectedKSK2070_R01List, removeSelectedKSK2070_R01List, initSelectedKSK2070_R01List,
		columnState, setColumnState,
		showSubtotals, setShowSubtotals,        // 통합 상태만 사용
		subtotalData, setSubtotalData,
		dateSubtotalData, setDateSubtotalData,   // 생산일자별 소계 데이터 추가
		gridRowData, setGridRowData,
	} = useKSK2070_R01Store();

	const { 
		jobTeamList,
		ordTypeList,
		unitList,
		compCdList, 
		employeeCdList,
		workPlaceList,
	} = useCommonCodeStore();

	// 코드명 빠른 조회를 위한 맵 구성
	const codeMaps = useMemo(() => {
		return {
			ordType: new Map(ordTypeList.map(v => [v.minorCd, v.codeNm])),
			workPlace: new Map(workPlaceList.map(v => [v.workplaceCd, v.workplaceNm])),
			unit: new Map(unitList.map(v => [v.minorCd, v.codeNm])),
			emp: new Map(employeeCdList.map(v => [v.empNo, v.empNm])),
			comp: new Map(compCdList.map(v => [v.compCd, v.compNm])),
		};
	}, [ordTypeList, workPlaceList, unitList, employeeCdList, compCdList]);


	const {
		getRowId,
		paddedGridData,
		createRowClassRules,
		createCellClassRules,
		rowIndexCellButtonType1,
		rowIndexCellButtonType2,
		handleSwitchButtonHandler,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'productResultNo',
		gridData: gridRowData || [], // 빈 배열 방지
		defaultGridData: defaultKSK2070_R01Dto as GridRowData,
		selectedRow: selectedKSK2070_R01List,
	});



	// getRowId 함수를 GridRowData 타입에 맞게 수정
	const getRowIdForGrid = useCallback((params: any) => {
		if (isSubtotalRow(params.data)) {
			// 소계 행은 itemCd + '_subtotal'로 고유 ID 생성
			if (isItemSubtotalRow(params.data)) {
				return `${params.data.itemCd}_item_subtotal`;
			} else if (isDateSubtotalRow(params.data)) {
				return `${params.data.itemCd}_${params.data.productDt}_date_subtotal`;
			}
			return `${params.data.itemCd}_subtotal`;
		}
		// 상세 행은 기존 productResultNo 사용
		return params.data?.productResultNo || params.id;
	}, []);

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);


	// 통합 소계 데이터 계산 및 그리드 데이터 생성
	useEffect(() => {
		if (KSK2070_R01List && KSK2070_R01List.length > 0) {
			// 품목별 소계 계산
			const subtotals = calculateSubtotals(KSK2070_R01List);
			setSubtotalData(subtotals);
			
			// 생산일자별 소계 계산
			const dateSubtotals = calculateDateSubtotals(KSK2070_R01List);
			setDateSubtotalData(dateSubtotals);
			
			// 통합 병합 (showSubtotals 하나의 상태로 제어)
			const mergedData = mergeDataWithSubtotals(
				KSK2070_R01List, 
				subtotals, 
				dateSubtotals,
				showSubtotals  // 단일 상태로 통합
			);
			setGridRowData(mergedData);
		} else {
			setSubtotalData([]);
			setDateSubtotalData([]);
			setGridRowData([]);
		}
	}, [KSK2070_R01List, showSubtotals, setSubtotalData, setDateSubtotalData, setGridRowData]);

	// 소계 표시 상태 변경시 그리드 데이터 재생성
	useEffect(() => {
		if (KSK2070_R01List && KSK2070_R01List.length > 0 && 
			(subtotalData.length > 0 || dateSubtotalData.length > 0)) {
			const mergedData = mergeDataWithSubtotals(
				KSK2070_R01List, 
				subtotalData, 
				dateSubtotalData,
				showSubtotals  // 단일 상태로 통합
			);
			setGridRowData(mergedData);
		}
	}, [showSubtotals, KSK2070_R01List, subtotalData, dateSubtotalData, setGridRowData]);

    // 컬럼별 기본 헤더명 매핑
    const headerNames: Record<string, string> = {
        rowIndex: '',
        productResultNo: '생산실적번호',
        compCd: '사업장',
        compNm: '회사명',
        productDt: '생산일자',
        orderType: '작업구분',
        orderTypeNm: '주문유형명',
        workplaceCd: '공정명',
        workplaceNm: '공정명',
        jobTeam: '작업팀',
        jobTeamNm: '작업팀명',
        itemCd: '품목코드',
		itemNm: '품명',
        itemSpec: '규격',
        itemUnit: '단위',
        orderQty: '지시량',
        productEmp: '작업자',
        empNm: '작업자명',
        productQty: '생산량',
        badQty: '불량',
        acceptQty: '실적량',
    };

	const getCellStyle = (params: any, key: string) => {
		// [수정] 숫자 컬럼 정의
		const numericFields = ['orderQty', 'productQty', 'badQty', 'acceptQty', 'badRate'];
		const isNumericColumn = numericFields.includes(key);

		// 소계 행인지 확인
		if (isSubtotalRow(params.data)) {
			// isHeaderCells 체크 불필요 (cellStyle은 헤더에 적용되지 않음)
			
			if (isItemSubtotalRow(params.data)) {
				// 품목별 소계 행 스타일
				return {
					backgroundColor: '#f0f8ff',
					color: '#2c5aa0',
					fontWeight: 'bold',
					borderTop: '2px solid #2c5aa0',
					borderBottom: '1px solid #2c5aa0',
					textAlign: isNumericColumn ? 'right' : 'left', // 우측 정렬
				};
			} else if (isDateSubtotalRow(params.data)) {
				// 생산일자별 소계 행 스타일
				return {
					backgroundColor: '#fff8f0',
					color: '#a05a2c',
					fontWeight: 'bold',
					borderTop: '1px solid #a05a2c',
					borderBottom: '1px solid #a05a2c',
					textAlign: isNumericColumn ? 'right' : 'left', // 우측 정렬
				};
			}
		}

		// [수정] 숫자 컬럼인 경우 onlyTouchGridStyle을 거치지 않고 직접 스타일 반환
		if (isNumericColumn) {
			return {  cursor: 'default',  textAlign: 'right', justifyContent: 'flex-end' };
		}
		
		// SYS2010 패턴 적용 - 공통 함수 사용 (일반 행)
		const baseStyle = onlyTouchGridStyle(
			key,
			'productResultNo',
		)(params);
		
		return baseStyle;
	}

	const baseColumnDefs: ColDef<GridRowData>[] = useMemo(() => {
		const result: ColDef<GridRowData>[] = [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: '생산실적번호', colId: 'productResultNo', field: 'productResultNo' as keyof GridRowData, width: 160, 
				cellStyle: (params: any) => getCellStyle(params, 'productResultNo'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					return params.data?.productResultNo || '';
				}
			},
			{
				headerName: '사업장', colId: 'compCd', field: 'compCd' as keyof GridRowData, width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'compCd'), 
				cellRenderer: (params: any) => {
					if (isItemSubtotalRow(params.data)) {
						const itemCd = params.data.itemCd;
						const count = params.data.count;
						return `${itemCd} 소계 (총 ${count}건)`;
					} else if (isDateSubtotalRow(params.data)) {
						const productDt = params.data.productDt;
						const dateCount = params.data.dateCount;
						let formattedDate = '';
						if (productDt) {
							const date = new Date(productDt);
							if (!isNaN(date.getTime())) {
								formattedDate = formatDate(date);
							}
						}
						return ` ${formattedDate} (${dateCount}건)`;
					}
					const compNm = codeMaps.comp.get(params.data?.compCd);
					return compNm || params.data?.compCd || '';
				},
				...CompCdColumn.options(compCdList, false, undefined, undefined),
			},
			{
				headerName: '생산일자', colId: 'productDt', field: 'productDt' as keyof GridRowData, width: 120, 
				cellStyle: (params: any) => getCellStyle(params, 'productDt'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					if (params.data?.productDt) {
						const date = new Date(params.data.productDt);
						if (!isNaN(date.getTime())) {
							return formatDate(date);
						}
					}
					return '';
				},
				valueFormatter: (params: any) => {
					if (params.data?.productDt) {
						const date = new Date(params.data.productDt);
						if (!isNaN(date.getTime())) {
							return formatDate(date);
						}
					}
					return '';
				},
				comparator: cellDateComparator,	
			},
			{
				headerName: '작업구분', colId: 'orderType', field: 'orderType' as keyof GridRowData, width: 90, 
				cellStyle: (params: any) => getCellStyle(params, 'orderType'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					return codeMaps.ordType.get(params.data?.orderType) || '';
				},
				valueGetter: (params: any) => codeMaps.ordType.get(params.data?.orderType) || '',
			},
			{
				headerName: '공정명', colId: 'workplaceCd', field: 'workplaceCd' as keyof GridRowData, width: 120,
				cellStyle: (params: any) => getCellStyle(params, 'workplaceCd'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					return codeMaps.workPlace.get(params.data?.workplaceCd) || '';
				},
				valueGetter: (params: any) => codeMaps.workPlace.get(params.data?.workplaceCd) || '',
			},
			{
				headerName: '작업팀', colId: 'jobTeam', field: 'jobTeam' as keyof GridRowData, width: 75, 
				cellStyle: (params: any) => getCellStyle(params, 'jobTeam'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					return jobTeamList.find(v => v.minorCd=== params.data?.jobTeam)?.codeNm || '';
				},
				valueGetter: (params: any) => jobTeamList.find(v => v.minorCd=== params.data?.jobTeam)?.codeNm || '',
			},
			{
				headerName: '품목코드', colId: 'itemCd', field: 'itemCd' as keyof GridRowData, width: 110, 
				cellStyle: (params: any) => getCellStyle(params, 'itemCd'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (params.node.rowPinned === 'bottom') return '';
					return params.data?.itemCd || '';
				}
			},
			{
				headerName: '품명', colId: 'itemNm', field: 'itemNm' as keyof GridRowData, width: 200, 
				cellStyle: (params: any) => getCellStyle(params, 'itemNm'), 
				editable: false,
				valueFormatter: (params: any) => params.data?.itemNm || '',
			},
			{
				headerName: '규격', colId: 'itemSpec', field: 'itemSpec' as keyof GridRowData, width: 200, 
				cellStyle: (params: any) => getCellStyle(params, 'itemSpec'), 
				editable: false,
				valueFormatter: (params: any) => params.data?.itemSpec || '',
			},
			{
				headerName: '단위', colId: 'itemUnit', field: 'itemUnit' as keyof GridRowData, width: 80, 
				cellStyle: (params: any) => getCellStyle(params, 'itemUnit'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					return codeMaps.unit.get(params.data?.itemUnit) || '';
				},
				valueGetter: (params: any) => isSubtotalRow(params.data) ? '' : (codeMaps.unit.get(params.data?.itemUnit) || ''),
				...CommonCodeColumn.options(unitList, false, undefined, undefined),
			},
			{
				headerName: '지시량', colId: 'orderQty', field: 'orderQty' as keyof GridRowData, width: 75, 
				cellStyle: (params: any) => getCellStyle(params, 'orderQty'), 
				editable: false,
				type: 'numericColumn',
				valueFormatter: (p: any) => formatNumber(p.value || 0),
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return formatNumber(params.data.orderQty || 0);
					return params.valueFormatted ?? (params.value ?? '');
				},
			},
			{
				headerName: '작업자', colId: 'empNm', field: 'empNm' as keyof GridRowData, width: 80,
				cellStyle: (params: any) => getCellStyle(params, 'empNm'), 
				editable: false,
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return '';
					if (params.node.rowPinned === 'bottom') return '';
					return codeMaps.emp.get(params.data?.productEmp) || '';
				},
				valueGetter: (params: any) => isSubtotalRow(params.data) ? '' : (codeMaps.emp.get(params.data?.productEmp) || ''),
			},
			{
				headerName: '생산량', colId: 'productQty', field: 'productQty' as keyof GridRowData, width: 90,
				cellStyle: (params: any) => getCellStyle(params, 'productQty'), 
				editable: false,
				type: 'numericColumn',
				valueFormatter: (p: any) => formatNumber(p.value || 0),
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return formatNumber(params.data.productQty || 0);
					return params.valueFormatted ?? (params.value ?? '');
				},
				headerClass: aggridHeaderClass,
			},
			{
				headerName: '불량', colId: 'badQty', field: 'badQty' as keyof GridRowData, width: 80,
				cellStyle: (params: any) => getCellStyle(params, 'badQty'), 
				editable: false,
				type: 'numericColumn',
				valueFormatter: (p: any) => formatNumber(p.value || 0),
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return formatNumber(params.data.badQty || 0);
					return params.valueFormatted ?? (params.value ?? '');
				},
				headerClass: aggridHeaderClass,
			},
			{
				headerName: '실적량', colId: 'acceptQty', field: 'acceptQty' as keyof GridRowData, width: 100,
				cellStyle: (params: any) => getCellStyle(params, 'acceptQty'), 
				editable: false,
				type: 'numericColumn',
				valueFormatter: (p: any) => formatNumber(p.value || 0),
				cellRenderer: (params: any) => {
					if (isSubtotalRow(params.data)) return formatNumber(params.data.acceptQty || 0);
					return params.valueFormatted ?? (params.value ?? '');
				},
				headerClass: aggridHeaderClass,
			}
			,
			{
				headerName: '불량률(%)', colId: 'badRate', field: 'badRate' as keyof GridRowData, width: 110,
				cellStyle: (params: any) => {
					const base = getCellStyle(params, 'badRate') as any;
					return { ...(base || {}), textAlign: 'right' };
				}, 
				editable: false,
				type: 'numericColumn',
				valueGetter: (params: any) => {
					const data = params.data || {};
					const productQty = Number(data.productQty) || 0;
					const badQty = Number(data.badQty) || 0;
					if (!isFinite(productQty) || productQty <= 0 || !isFinite(badQty) || badQty < 0) return 0;
					return (badQty / productQty) * 100;
				},
				valueFormatter: (p: any) => `${(Number(p.value) || 0).toFixed(2)}`,
				headerClass: aggridHeaderClass,
			}
		];
		
		
		return result;
	}, [hoveredRowIndex, gridRowData, selectedKSK2070_R01List, compCdList, ordTypeList, unitList, employeeCdList, workPlaceList, codeMaps]);

	const columnDefs: ColDef<GridRowData>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<GridRowData>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass
		});
	}, [baseColumnDefs, columnState, headerNames]);

	// 합계행(pinned bottom) 스타일: PRD3030 전용
	const getRowStyleForPRD3030 = useCallback((params: any) => {
		if (params.node.rowPinned === 'bottom') {
			return { background: '#F0F0F0', fontWeight: 'bold', borderTop: 'none' };
		}
		return undefined;
	}, []);

	const handleRowClick = useCallback((e: RowClickedEvent<GridRowData>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;
		
		// 소계 행 클릭시 아무 동작하지 않음
		if (isSubtotalRow(rowData)) {
			return;
		}
		
		// 일반 행 클릭 처리 (기존 로직)
		const isSelected = selectedKSK2070_R01List.includes(rowData.productResultNo);
		
		if (isSelected) {
			removeSelectedKSK2070_R01List([rowData.productResultNo]);
		} else {
			addSelectedKSK2070_R01List([rowData.productResultNo]);
		}
	}, [selectedKSK2070_R01List, addSelectedKSK2070_R01List, removeSelectedKSK2070_R01List, gridRowData]);

    const pinnedBottomRow = useMemo(() => {
        // 숫자 필드들의 실제 합계 계산 (소계 행 제외)
        const detailRows = gridRowData.filter(row => !isSubtotalRow(row));
        const totals = detailRows.reduce((acc, item) => {
            return {
                orderQty: acc.orderQty + (Number(item.orderQty) || 0),
                productQty: acc.productQty + (Number(item.productQty) || 0),
                badQty: acc.badQty + (Number(item.badQty) || 0),
                acceptQty: acc.acceptQty + (Number(item.acceptQty) || 0),
				editable: false,
            };
        }, { orderQty: 0, productQty: 0, badQty: 0, acceptQty: 0, editable: false });

        return [{
            rowIndex: '',
            productResultNo: detailRows.length.toLocaleString() + "건",
            orderQty: totals.orderQty,
            productQty: totals.productQty,
            badQty: totals.badQty,
            acceptQty: totals.acceptQty,
            editable: false,
        }];
    }, [gridRowData]);

	useEffect(() => { ensureSelectedRow(); }, [gridRowData, ]);

	// 행 드래그: 드랍 후 순서 재계산 처리
	const handleRowDragEnd = useCallback((e: any) => {
		const api = gridRef.current?.api || e?.api;
		if (!api) return;
		
		if (api.applyColumnState) {
			api.applyColumnState({ defaultState: { sort: null as any, sortIndex: null as any } });
		}
		
		const reordered: GridRowData[] = [];
		api.forEachNodeAfterFilterAndSort((node: any, idx: number) => {
			if (!node?.data) return;
			reordered.push({
				...node.data,
				orderIndex: idx + 1,
				orderIndexSeq: 0,
			} as GridRowData);
		});
		
		// 스토어를 통해 데이터 업데이트
		setGridRowData(reordered);
	}, [gridRef, setGridRowData]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            {/* GridContextMenuWrapper로 모든 기능 활성화 */}
            <GridContextMenuWrapper<GridRowData>
				gridRef={gridRef}
				paddedGridData={gridRowData}
				initSelected={initSelectedKSK2070_R01List}
				baseColumnDefs={columnDefs}
				setColumnState={setColumnState}
				localStorageKey={COLUMN_STATE_KEY}
				enableRowDrag={true}
				orderIndexField="orderIndex"
				orderIndexSeqField="orderIndexSeq"
				onAfterReorder={handleRowDragEnd}
				// 기능 옵션 설정
				enableDeleteAddedRow={true}
				enableColumnReset={true}
				enableAutoSize={true}
				enableSelectionReset={true}
				height={'100%'}
			>
                <Grid_Touch<GridRowData>
					
					ref={gridRef}
					rowData={paddedGridData}
					columnDefs={columnDefs}			
					defaultColDef={defaultColDef}
					getRowId={getRowId}
					
					rowClassRules={createRowClassRules<GridRowData>({
						idKey: 'productResultNo',
						selectedList: selectedKSK2070_R01List,
						deletedList: [],
						
					})}
					onRowClicked={handleRowClick}
					onCellKeyDown={undefined}
					pinnedBottomRowData={pinnedBottomRow}    

					// Grid_Select에 공통 로직 처리를 위한 props 전달
					setHoveredRowIndex={setHoveredRowIndex}		//마우스 호버 상태 이벤트를 위한 저장
					columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
					setColumnState={setColumnState}				//컬럼 상태 저장
                />
			</GridContextMenuWrapper>
        </div>
    );
}
